import { useEffect, useState } from "react"
import BasicTile from "../Tile/BasicTile"
import { FileDropZone, Icon } from "web/components"
import { useTile } from "web/hooks/data"
import { openSSRTileSettingsModal, supportsSSRSettingsModal } from "../helpers"
import classNames from "classnames"
import useTileSettingsModal from "web/hooks/data/useTileSettingsModal"
import TileGallerySettingsModal from "./TileGallerySettingsModal"

export default function GalleryTile({
  canEdit = true,
  photos,
  isDropzoneDisabled,
  isTemplate,
  tileFetchParams,
  overview,
  subjectType,
  subjectId,
}) {
  const tile = useTile({
    tileFetchParams,
  })

  const reactSettingsModal = useTileSettingsModal({
    overview,
    isTemplate,
    tileSettingsComponent: TileGallerySettingsModal,
    tileTitle: "Gallery Settings",
    tileFetchParams,
  })

  const tileSettingsModal = supportsSSRSettingsModal(subjectType, "galleries")
    ? openSSRTileSettingsModal({
        isTemplate,
        overviewId: overview.id,
        tileId: tileFetchParams.id,
        tileType: "galleries",
        subjectType,
        subjectId,
      })
    : reactSettingsModal

  const [photoIds, setPhotoIds] = useState([])
  const [photoId, setPhotoId] = useState({})

  function findPhotoWithId(photo_id) {
    return photos.find((photo) => photo.id === photo_id)
  }

  function findPointerWithId(photo_id) {
    return photoIds.find((pointer_id) => pointer_id === photo_id)
  }

  const tileHasDefaultPhoto = tile.selected_image_attachment_id !== null
  const hasPhotos = photos.length !== 0
  const photoIndex = photos.indexOf(findPhotoWithId(photoId))
  const hasPrevious = photoIndex !== 0
  const hasNext = photoIndex < photos.length - 1
  const photo = findPhotoWithId(photoId)

  useEffect(() => {
    const initialPhotoIds = photos.map((photo) => {
      return photo.id
    })

    setPhotoIds(initialPhotoIds)
    setPhotoId(initialPhotoIds[0])

    const foundPhoto = findPhotoWithId(tile.selected_image_attachment_id)
    const foundDefaultImageOnLoad =
      hasPhotos && tileHasDefaultPhoto && foundPhoto
    const isPhotoDeleted =
      findPointerWithId(tile.selected_image_attachment_id) === undefined
    const nextPhotoId = findNextPhotoId(tile.selected_image_attachment_id)
    const idNotFound = findPointerWithId(tile.selected_image_attachment_id)

    if (idNotFound) {
      tile.update({
        overview_tile: {
          selected_image_attachment_id: idNotFound,
        },
      })
    }

    if (foundDefaultImageOnLoad) {
      setPhotoId(foundPhoto.id)
      tile.update({
        overview_tile: {
          selected_image_attachment_id: foundPhoto,
        },
      })
    }

    if (isPhotoDeleted && nextPhotoId && tileHasDefaultPhoto) {
      tile.update({
        overview_tile: {
          selected_image_attachment_id: nextPhotoId,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos])

  useEffect(() => {
    if (
      tile.selected_image_attachment_id &&
      !findPhotoWithId(tile.selected_image_attachment_id)
    ) {
      tile.update({
        overview_tile: {
          selected_image_attachment_id: "",
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos])

  async function handleOnHide() {
    await tile.deleteTile()
  }

  async function handleAddPhoto(fileList) {
    return photos.add(Array.from(fileList))
  }

  function calculateOperation(foundPhotoIndex) {
    if (foundPhotoIndex === -1) return +1
    const firstElement = foundPhotoIndex === 0

    if (firstElement) {
      return +1
    } else {
      return -1
    }
  }

  function findNextPhotoId(id, operation) {
    const foundPhotoIndex = photoIds.indexOf(id)
    const calculatedOperation = !!operation
      ? operation
      : calculateOperation(foundPhotoIndex)

    const foundNextPhotoId = photoIds[foundPhotoIndex + calculatedOperation]

    return foundNextPhotoId
  }

  function updateCurrentPhoto(id, operation) {
    const foundNextPhotoId = findNextPhotoId(id, operation)
    setPhotoId(foundNextPhotoId)

    // As user clicks through the carousell, save the image displayed
    tile.update({
      overview_tile: {
        selected_image_attachment_id: foundNextPhotoId,
      },
    })
  }

  const shouldDisableDropZone = isTemplate || !canEdit || isDropzoneDisabled

  if (isTemplate || (!canEdit && !hasPhotos)) {
    return (
      <div className="h-full">
        <BasicTile
          canEdit={canEdit}
          handleTileSettings={isTemplate ? tileSettingsModal : undefined}
          handleOnHide={handleOnHide}
          id={tileFetchParams.id}
        >
          <div className="flex h-full items-center justify-center">
            <div>
              <Icon
                name="camera"
                outline="true"
                width="64px"
                height="64px"
                className="m-auto flex justify-center text-neutral-7"
              />
              <p className="text-center text-sm text-neutral-7">Gallery</p>
            </div>
          </div>
        </BasicTile>
      </div>
    )
  } else if (!hasPhotos) {
    return (
      <div className="h-full">
        <BasicTile
          canEdit={canEdit}
          handleOnHide={handleOnHide}
          id={tileFetchParams.id}
        >
          <FileDropZone
            onFilesDropped={handleAddPhoto}
            kind="photos"
            border={false}
            disabled={shouldDisableDropZone}
          />
        </BasicTile>
      </div>
    )
  } else {
    const canShowPrevious = hasPrevious && canEdit
    const canShowNext = hasNext && canEdit
    const ARROW_CLASSES =
      "invisible arrow inline-flex items-center justify-center size-10 rounded-full bg-white/90 group-hover:visible"

    return (
      <BasicTile
        canEdit={canEdit}
        handleTileSettings={tileSettingsModal}
        handleOnHide={handleOnHide}
      >
        <>
          <img
            className="h-full w-full rounded object-cover"
            id="thumbnail-gallery-current-photo"
            alt="current thumbnail"
            src={photo ? photo.thumbnail_url : ""}
          />
          <div
            className={classNames(
              "group absolute inset-0 grid items-center px-2",
              { "cursor-pointer": canEdit }
            )}
          >
            {canShowPrevious && (
              <div
                className={ARROW_CLASSES}
                onClick={() => updateCurrentPhoto(photoId, -1)}
              >
                <Icon
                  className="w-6"
                  name={"chevron-left"}
                  alt="previous thumbnail"
                />
              </div>
            )}
            {canShowNext && (
              <div className="absolute right-2">
                <div
                  className={ARROW_CLASSES}
                  onClick={() => updateCurrentPhoto(photoId, +1)}
                >
                  <Icon
                    className="w-6"
                    name={"chevron-right"}
                    alt="next thumbnail"
                  />
                </div>
              </div>
            )}
          </div>
        </>
      </BasicTile>
    )
  }
}
